import React, { useState, useEffect } from 'react';
import { 
  Search, 
  DollarSign, 
  Users, 
  XCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Phone,
  Send,
  Wifi,
  WifiOff,
  Mail,
  FileText,
  Zap
} from 'lucide-react';
import apiService from '../services/apiService';
import { formatarMensagemPix } from '../utils/whatsappFormatter';
import { useLinkPixStorage } from '../hooks/useLocalStorage';
import { pixPopupUtils } from '../utils/popupUtils';


export default function LinkPixAleatorio() {
  // Estados
  const [busca, setBusca] = useState('');
  const [tipoBusca, setTipoBusca] = useState('nome');
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);
  
  // Modal de geração de link
  const [modalAberto, setModalAberto] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [valorLink, setValorLink] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [diasExpiracao, setDiasExpiracao] = useState(1);
  const [loadingLink, setLoadingLink] = useState(false);
  
  // Estado de conexão WhatsApp
  const [whatsappConectado, setWhatsappConectado] = useState(false);
  const [verificandoWhatsapp, setVerificandoWhatsapp] = useState(true);

  // ⭐ Hook de localStorage para salvar dados do link PIX
  const { salvarLinkPix } = useLinkPixStorage();

  /**
   * ⭐ VERIFICAR CONEXÃO WHATSAPP AO CARREGAR
   */
  useEffect(() => {
    verificarConexaoWhatsApp();
    const interval = setInterval(verificarConexaoWhatsApp, 30000);
    return () => clearInterval(interval);
  }, []);

  const verificarConexaoWhatsApp = async () => {
    try {
      const response = await apiService.whatsapp.getStatus();
      const conectado = response?.data?.success && response?.data?.data?.connected === true;
      setWhatsappConectado(conectado);
      setVerificandoWhatsapp(false);
    } catch (error) {
      console.error('❌ Erro ao verificar WhatsApp:', error);
      setWhatsappConectado(false);
      setVerificandoWhatsapp(false);
    }
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return '';
    const numero = telefone.replace(/\D/g, '');
    if (numero.length === 11) {
      return numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (numero.length === 10) {
      return numero.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numero;
  };

  const limparTelefone = (telefone) => {
    if (!telefone) return '';
    let numero = telefone.replace(/\D/g, '');
    if (numero.length === 11 && numero[0] === '0') {
      numero = numero.substring(1);
    }
    if (numero.length === 10 && ['6', '7', '8', '9'].includes(numero[2])) {
      numero = numero.substring(0, 2) + '9' + numero.substring(2);
    }
    if (numero.length === 11 && !numero.startsWith('55')) {
      numero = '55' + numero;
    }
    return numero;
  };

  const formatarCpfCnpj = (valor) => {
    if (!valor) return '';
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 11) {
      return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/\D/g, '');
    if (cpf.length !== 11) return false;
    if (/^(\d)\1+$/.test(cpf)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = (soma * 10) % 11;
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;
    return true;
  };

  const validarCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1+$/.test(cnpj)) return false;
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += numeros.charAt(tamanho - i) * pos--;
      if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    return true;
  };

  const validarCpfCnpj = (valor) => {
    if (!valor) return false;
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length === 11) {
      return validarCPF(numeros);
    } else if (numeros.length === 14) {
      return validarCNPJ(numeros);
    }
    return false;
  };

  const validarEmail = (email) => {
    if (!email) return true;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const buscarClientes = async () => {
    if (!busca.trim()) {
      setErro('Digite um nome ou código para buscar');
      return;
    }

    setLoading(true);
    setErro(null);
    setClientes([]);

    try {
      const response = await apiService.clientes.buscar(busca, 10, 0);
      let clientesEncontrados = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          clientesEncontrados = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          clientesEncontrados = response.data.data;
        }
      }

      if (clientesEncontrados.length === 0) {
        setErro('Nenhum cliente encontrado');
        setLoading(false);
        return;
      }

      clientesEncontrados = clientesEncontrados.slice(0, 10);
      setClientes(clientesEncontrados);
      
    } catch (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      setErro('Erro ao buscar clientes: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const abrirModalLink = (cliente) => {
    setClienteSelecionado(cliente);
    setValorLink('');
    const timestamp = Date.now();
    const sequencia = `${cliente.Codigo}-${timestamp}`;
    setMensagem(`Referente a pagamento via Pix da compra com sequência ${sequencia}`);
    const telefoneCliente = cliente.Fone_1 || cliente.Fone_2 || '';
    const telefoneFormatado = formatarTelefone(telefoneCliente);
    setCelular(telefoneFormatado);
    setEmail('');
    const cpfCnpjCliente = cliente.CPF || cliente.CNPJ || '';
    const cpfCnpjFormatado = formatarCpfCnpj(cpfCnpjCliente);
    setCpfCnpj(cpfCnpjFormatado);
    setDiasExpiracao(1);
    setModalAberto(true);
  };

  /**
   * ⭐ FUNÇÃO PRINCIPAL - GERAR LINK PIX
   * GARANTIA: A janela SEMPRE abre, independente dos envios
   */
  const gerarLinkPix = async () => {
    // Validações
    if (!valorLink || parseFloat(valorLink) <= 0) {
      setErro('Digite um valor válido');
      return;
    }

    if (!mensagem || mensagem.trim() === '') {
      setErro('Mensagem/Descrição é obrigatória');
      return;
    }

    const temCelular = celular && celular.trim() !== '';
    if (temCelular) {
      const celularLimpo = limparTelefone(celular);
      if (celularLimpo.length < 12 || celularLimpo.length > 13) {
        setErro('Número de celular inválido');
        return;
      }
    }

    const temEmail = email && email.trim() !== '';
    if (temEmail && !validarEmail(email)) {
      setErro('Email inválido');
      return;
    }

    if (!cpfCnpj || cpfCnpj.trim() === '') {
      setErro('CPF ou CNPJ é obrigatório');
      return;
    }

    if (!validarCpfCnpj(cpfCnpj)) {
      setErro('CPF ou CNPJ inválido');
      return;
    }

    if (!diasExpiracao || diasExpiracao < 1 || diasExpiracao > 30) {
      setErro('Dias de expiração deve estar entre 1 e 30');
      return;
    }

    setLoadingLink(true);
    setErro(null);

    try {
      const celularLimpo = temCelular ? limparTelefone(celular) : undefined;
      
      const dados = {
        ordem_cliente: clienteSelecionado.Ordem,
        codigo_cliente: clienteSelecionado.Codigo,
        nome_cliente: clienteSelecionado.Nome,
        cpf_cnpj: cpfCnpj.replace(/\D/g, ''),
        valor: parseFloat(valorLink),
        descricao: mensagem,
        dias_expiracao: diasExpiracao,
        link_aleatorio: true
      };

      if (temCelular && celularLimpo) {
        dados.celular = celularLimpo;
      }

      if (temEmail && email) {
        dados.email = email;
      }

      console.log('💰 Enviando dados para API:', dados);

      const response = await apiService.pix.criar(dados);

      console.log('📥 Resposta da API PIX:', response);

      if (response.success || response.data?.success) {
        const linkData = response.data?.data || response.data;
        
        console.log('✅ Link PIX criado! ID:', linkData.link_id);
        console.log('📦 Estrutura completa do linkData:');
        console.log('   - link_id:', linkData.link_id);
        console.log('   - pedido_id:', linkData.pedido_id);
        console.log('   - checkout_url:', linkData.checkout_url);
        console.log('   - pix.qr_code:', linkData.pix?.qr_code ? 'PRESENTE ✅' : 'AUSENTE ❌');
        console.log('   - pix.qr_code_url:', linkData.pix?.qr_code_url ? 'PRESENTE ✅' : 'AUSENTE ❌');
        console.log('   - pix.expires_at:', linkData.pix?.expires_at);
        
        let statusEnvio = {
          whatsapp: false,
          email: false
        };

        // ⭐ ENVIOS ASSÍNCRONOS E NÃO BLOQUEANTES
        const promessasEnvio = [];

        // O envio do WhatsApp já é feito automaticamente pelo backend quando o celular é fornecido
        // na criação do link PIX, então apenas marcamos como enviado
        if (temCelular && whatsappConectado && celularLimpo) {
          console.log('📱 WhatsApp será enviado automaticamente pelo backend');
          statusEnvio.whatsapp = true;
        }

        if (temEmail) {
          const enviarEmail = (async () => {
            try {
              console.log('📧 Iniciando envio Email...');
              
              // ⭐ BUSCAR checkout_url EM VÁRIAS LOCALIZAÇÕES POSSÍVEIS
              const checkoutUrl = linkData.checkout_url 
                || linkData.pix?.checkout_url 
                || linkData.data?.checkout_url
                || linkData.link;
              
              if (!checkoutUrl) {
                console.warn('⚠️ checkout_url não disponível em nenhuma estrutura');
                console.warn('   Estrutura linkData:', Object.keys(linkData));
                console.warn('   linkData.pix:', linkData.pix ? Object.keys(linkData.pix) : 'undefined');
                return;
              }
              
              console.log('✅ checkout_url encontrado:', checkoutUrl.substring(0, 50) + '...');
              
              const dadosEmail = {
                para: email,
                assunto: 'Link de Pagamento PIX - Dantas Eletro',
                ordem_cliente: clienteSelecionado.Ordem,
                nome_cliente: clienteSelecionado.Nome,
                valor: parseFloat(valorLink),
                link: checkoutUrl, // ✅ GARANTIDO QUE EXISTE
                qr_code: linkData.pix?.qr_code,
                descricao: mensagem,
                data_vencimento: linkData.pix?.expires_at || new Date().toISOString(),
                expires_at: linkData.pix?.expires_at
              };
              
              console.log('📤 Enviando email para:', dadosEmail.para);
              
              const responseEmail = await apiService.email.enviar(dadosEmail);
              if (responseEmail?.success || responseEmail?.data?.success) {
                statusEnvio.email = true;
                console.log('✅ Email enviado!');
              }
            } catch (err) {
              console.error('❌ Erro Email:', err);
              console.error('   Detalhes:', err.response?.data || err.message);
            }
          })();
          promessasEnvio.push(enviarEmail);
        }

        // ⭐ AGUARDAR ENVIOS (NÃO BLOQUEIA)
        if (promessasEnvio.length > 0) {
          console.log('⏳ Aguardando', promessasEnvio.length, 'envios...');
          await Promise.allSettled(promessasEnvio);
          console.log('✅ Envios concluídos. Status:', statusEnvio);
        }

        // ⭐ PREPARAR E SALVAR DADOS
        const dadosParaJanela = {
          ...linkData,
          cliente: {
            nome: clienteSelecionado.Nome,
            codigo: clienteSelecionado.Codigo,
            ordem: clienteSelecionado.Ordem,
            cpf_cnpj: cpfCnpj,
            telefone: celular,
            email: email
          },
          valor_formatado: valorLink,
          descricao: mensagem,
          dias_expiracao: diasExpiracao,
          status_envio: statusEnvio
        };

        salvarLinkPix(dadosParaJanela);
        console.log('💾 Dados salvos no localStorage');

        // ⭐ ABRIR JANELA COM UTILITÁRIO MELHORADO
        console.log('🪟 Abrindo popup com utilitário...');
        
        const resultadoPopup = await pixPopupUtils.openResultadoAleatorio(dadosParaJanela);
        
        // Mensagem de sucesso baseada no resultado do popup
        let msg = '';
        if (resultadoPopup.success) {
          msg = 'Link PIX gerado! Popup aberto com sucesso. ✅';
          console.log('✅ Popup aberto com sucesso');
        } else {
          msg = 'Link PIX gerado! (Popup bloqueado - verifique as configurações do navegador) ✅';
          console.warn('⚠️ Popup bloqueado ou falha ao abrir');
        }
        
        // Adicionar informações de envio
        if (statusEnvio.whatsapp && statusEnvio.email) {
          msg += ' Enviado por WhatsApp e Email! 📱📧';
        } else if (statusEnvio.whatsapp) {
          msg += ' Enviado por WhatsApp! 📱';
        } else if (statusEnvio.email) {
          msg += ' Enviado por Email! 📧';
        }
        
        setSucesso(msg);
        
        // Fechar modal
        setTimeout(() => {
          fecharModal();
          setSucesso(null);
        }, 3000);
        
      } else {
        setErro('Erro ao gerar link PIX');
      }
    } catch (error) {
      console.error('❌ ERRO CRÍTICO:', error);
      setErro('Erro ao gerar link PIX: ' + error.message);
    } finally {
      setLoadingLink(false);
    }
  };

  const fecharModal = () => {
    setModalAberto(false);
    setClienteSelecionado(null);
    setValorLink('');
    setMensagem('');
    setCelular('');
    setEmail('');
    setCpfCnpj('');
    setDiasExpiracao(1);
    setErro(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      buscarClientes();
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Zap className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Link PIX Aleatório</h1>
              <p className="text-purple-100 mt-1">
                Gere links de pagamento PIX com valores personalizados
              </p>
            </div>
          </div>
          
          {/* INDICADOR WHATSAPP */}
          <div className="flex items-center space-x-2">
            {verificandoWhatsapp ? (
              <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Verificando...</span>
              </div>
            ) : whatsappConectado ? (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 rounded-lg border border-green-400">
                <Wifi className="w-5 h-5 text-green-300" />
                <span className="text-sm font-medium">WhatsApp Conectado</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 rounded-lg border border-red-400">
                <WifiOff className="w-5 h-5 text-red-300" />
                <span className="text-sm font-medium">WhatsApp Desconectado</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mensagens */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Erro</p>
            <p className="text-sm text-red-700 mt-1">{erro}</p>
          </div>
          <button onClick={() => setErro(null)} className="text-red-400 hover:text-red-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {sucesso && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">Sucesso</p>
            <p className="text-sm text-green-700 mt-1">{sucesso}</p>
          </div>
        </div>
      )}

      {/* Busca */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Search className="w-5 h-5 mr-2 text-gray-600" />
            Buscar Cliente
          </h2>
        </div>

        <div className="p-6">
          <div className="flex space-x-4">
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar por</label>
              <select
                value={tipoBusca}
                onChange={(e) => setTipoBusca(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="nome">Nome</option>
                <option value="codigo">Código</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {tipoBusca === 'nome' ? 'Nome do cliente' : 'Código do cliente'}
              </label>
              <input
                type={tipoBusca === 'codigo' ? 'number' : 'text'}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={tipoBusca === 'nome' ? 'Digite o nome...' : 'Digite o código...'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={buscarClientes}
                disabled={loading}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center space-x-2 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Buscando...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    <span>Buscar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de clientes */}
      {clientes.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2 text-gray-600" />
              Clientes Encontrados ({clientes.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {clientes.map((cliente) => (
              <div 
                key={cliente.Ordem} 
                className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => abrirModalLink(cliente)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <Users className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{cliente.Nome}</h3>
                      <p className="text-sm text-gray-500">
                        Código: {cliente.Codigo} | CPF/CNPJ: {cliente.CPF || cliente.CNPJ || 'N/A'}
                      </p>
                      {(cliente.Fone_1 || cliente.Fone_2) && (
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <Phone className="w-3 h-3 mr-1" />
                          {formatarTelefone(cliente.Fone_1 || cliente.Fone_2)}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirModalLink(cliente);
                    }}
                    className="ml-4 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center space-x-2"
                  >
                    <Zap className="w-5 h-5" />
                    <span>Gerar Link PIX</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-800">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Zap className="w-6 h-6 mr-2" />
                  Gerar Link PIX Aleatório
                </h3>
                <button onClick={fecharModal} className="text-white hover:text-gray-200">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Info cliente */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Cliente</h4>
                  <p className="text-base font-semibold text-gray-800">{clienteSelecionado?.Nome}</p>
                  <p className="text-sm text-gray-600">Código: {clienteSelecionado?.Codigo}</p>
                </div>

                {/* CPF/CNPJ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CPF ou CNPJ * <span className="text-red-600">(Obrigatório)</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={cpfCnpj}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, '');
                        if (valor.length <= 14) {
                          setCpfCnpj(formatarCpfCnpj(valor));
                        }
                      }}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg text-base ${
                        cpfCnpj && !validarCpfCnpj(cpfCnpj) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {cpfCnpj && !validarCpfCnpj(cpfCnpj) && (
                    <p className="mt-2 text-xs text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      CPF ou CNPJ inválido
                    </p>
                  )}
                </div>

                {/* Celular */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Celular <span className="text-gray-500">(Opcional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={celular}
                      onChange={(e) => setCelular(e.target.value)}
                      placeholder="(85) 98765-4321"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 flex items-center">
                    {whatsappConectado ? (
                      <>
                        <Send className="w-3 h-3 mr-1 text-green-600" />
                        <span className="text-green-700">Será enviado automaticamente por WhatsApp</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3 mr-1 text-yellow-600" />
                        <span className="text-yellow-700">WhatsApp desconectado</span>
                      </>
                    )}
                  </p>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-gray-500">(Opcional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="cliente@exemplo.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base"
                    />
                  </div>
                </div>

                {/* Valor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor a Cobrar *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={valorLink}
                      onChange={(e) => setValorLink(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-lg font-semibold"
                      placeholder="0,00"
                    />
                  </div>
                </div>

                {/* Dias */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dias para Expiração *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={diasExpiracao}
                      onChange={(e) => {
                        const valor = parseInt(e.target.value);
                        if (valor >= 1 && valor <= 30) {
                          setDiasExpiracao(valor);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base"
                    />
                  </div>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem/Descrição * <span className="text-red-600">(Obrigatório)</span>
                  </label>
                  <textarea
                    value={mensagem}
                    onChange={(e) => setMensagem(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none"
                    placeholder="Ex: Referente a pagamento..."
                  />
                </div>

                {/* Botões */}
                <div className="flex space-x-3">
                  <button
                    onClick={fecharModal}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={gerarLinkPix}
                    disabled={
                      loadingLink || 
                      !valorLink || 
                      parseFloat(valorLink) <= 0 || 
                      !cpfCnpj ||
                      !validarCpfCnpj(cpfCnpj) ||
                      !mensagem ||
                      mensagem.trim() === ''
                    }
                    className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {loadingLink ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Gerando...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        <span>Gerar Link PIX</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
